"use client"

import { addToCart } from "@lib/data/cart"
import {
  getSystemBuilder,
  ProductBySku,
  SystemBuilderData,
} from "@lib/data/products"
import { useIntersection } from "@lib/hooks/use-in-view"
import { getProductPrice } from "@lib/util/get-product-price"
import { HttpTypes } from "@medusajs/types"
import { Button, Text } from "@modules/common/components/ui"
import Divider from "@modules/common/components/divider"
import FinancingMessage from "@modules/common/components/financing-message"
import ShipCutoffBanner from "@modules/common/components/ship-cutoff-banner"
import OptionSelect from "@modules/products/components/product-actions/option-select"
import { useOptionSelection } from "@modules/products/hooks/use-option-selection"
import { useParams, usePathname, useSearchParams } from "next/navigation"
import { useEffect, useMemo, useRef, useState } from "react"
import { convertToLocale } from "@lib/util/money"
import ProductPrice from "../product-price"
import BackorderPanel from "./backorder-panel"
import MobileActions from "./mobile-actions"
import RequiredFittings from "./required-fittings"
import UpsellModal from "./upsell-modal"
import SystemWizard from "@modules/products/components/system-wizard"
import { useRouter } from "next/navigation"

type ProductActionsProps = {
  product: HttpTypes.StoreProduct
  region: HttpTypes.StoreRegion
  disabled?: boolean
  upsellProducts?: ProductBySku[]
  isSystemRadiator?: boolean
  requiredFittings?: ProductBySku[]
}

export default function ProductActions({
  product,
  region,
  disabled,
  upsellProducts,
  isSystemRadiator,
  requiredFittings,
}: ProductActionsProps) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const [isAdding, setIsAdding] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)
  const [showUpsell, setShowUpsell] = useState(false)
  const [showWizard, setShowWizard] = useState(false)
  // Snapshot of the system-builder data matching the finish of the variant
  // the shopper actually added, taken at add time.
  const [wizardData, setWizardData] = useState<SystemBuilderData | null>(null)
  // Only show the upsell modal / wizard once per product page visit.
  const upsellShownRef = useRef(false)
  const countryCode = useParams().countryCode as string

  const {
    options,
    setOptionValue,
    selectedVariant,
    visibleOptionValues,
    isValidVariant,
    inStock,
  } = useOptionSelection(product)

  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString())
    const value = isValidVariant ? selectedVariant?.id : null

    if (params.get("v_id") === value) {
      return
    }

    if (value) {
      params.set("v_id", value)
    } else {
      params.delete("v_id")
    }

    router.replace(pathname + "?" + params.toString())
  }, [selectedVariant, isValidVariant])

  // Out of stock but purchasable: the shopper is placing a backorder, not a
  // regular order — surface the backorder panel and relabel the button.
  const isBackordered = useMemo(() => {
    if (!selectedVariant) {
      return false
    }

    return (
      Boolean(selectedVariant.manage_inventory) &&
      (selectedVariant.inventory_quantity ?? 0) <= 0 &&
      Boolean(selectedVariant.allow_backorder)
    )
  }, [selectedVariant])

  // "AE" SKUs and variants flagged in metadata ship with a free billet cap
  const hasFreeBilletCap = useMemo(() => {
    if (!selectedVariant) {
      return false
    }

    return (
      Boolean(selectedVariant.sku?.startsWith("AE")) ||
      Boolean(selectedVariant.metadata?.free_billet_cap)
    )
  }, [selectedVariant])

  // ORB-port radiators must leave with an inlet and an outlet fitting
  // (metadata.required_fittings, resolved server-side). Only in-stock
  // fittings are offered; add-to-cart stays locked until both are picked.
  const [inletFittingId, setInletFittingId] = useState("")
  const [outletFittingId, setOutletFittingId] = useState("")

  const needsFittings = (requiredFittings?.length ?? 0) > 0

  const fittingOptions = useMemo(
    () => (requiredFittings ?? []).filter((f) => f.in_stock),
    [requiredFittings]
  )

  const inletFitting =
    fittingOptions.find((f) => f.variant_id === inletFittingId) ?? null
  const outletFitting =
    fittingOptions.find((f) => f.variant_id === outletFittingId) ?? null
  const fittingsSelected =
    !needsFittings || (Boolean(inletFitting) && Boolean(outletFitting))

  const fittingsTotal =
    inletFitting && outletFitting
      ? (inletFitting.price ?? 0) + (outletFitting.price ?? 0)
      : 0

  // Cross-sell candidates: never the product being viewed, never out of stock.
  const upsellItems = useMemo(
    () =>
      (upsellProducts ?? []).filter(
        (p) => p.id !== product.id && p.in_stock
      ),
    [upsellProducts, product.id]
  )


  // Financing marketing wants the price shown above it: the selected
  // variant's calculated price, or the cheapest ("From") price before a
  // variant is picked. 0 (no price known) renders nothing.
  const financingAmount = useMemo(() => {
    const { variantPrice, cheapestPrice } = getProductPrice({
      product,
      variantId: selectedVariant?.id,
    })

    const price = selectedVariant?.id ? variantPrice : cheapestPrice

    return price?.calculated_price_number ?? 0
  }, [product, selectedVariant])

  const actionsRef = useRef<HTMLDivElement>(null)

  const inView = useIntersection(actionsRef, "0px")

  // add the selected variant to the cart
  const handleAddToCart = async () => {
    if (!selectedVariant?.id) return null
    if (needsFittings && (!inletFitting || !outletFitting)) return null

    setAddError(null)
    setIsAdding(true)

    try {
      await addToCart({
        variantId: selectedVariant.id,
        quantity: 1,
        countryCode,
      })

      // The radiator cannot ship without its fittings — add both picks
      // before opening the wizard/upsell so they already see them in cart.
      if (inletFitting && outletFitting) {
        if (inletFitting.variant_id === outletFitting.variant_id) {
          await addToCart({
            variantId: inletFitting.variant_id,
            quantity: 2,
            countryCode,
          })
        } else {
          await addToCart({
            variantId: inletFitting.variant_id,
            quantity: 1,
            countryCode,
          })
          await addToCart({
            variantId: outletFitting.variant_id,
            quantity: 1,
            countryCode,
          })
        }
      }

      if (!upsellShownRef.current) {
        // Payload trim: system-builder data is fetched on demand here (it
        // used to be serialized into every PDP load). Finish-aware via the
        // variant that was actually added.
        let systemData: SystemBuilderData | null = null
        if (isSystemRadiator && selectedVariant?.id) {
          systemData = await getSystemBuilder(
            product.id,
            selectedVariant.id,
            region.id
          )
          const hasOffers =
            systemData &&
            (systemData.shrouds.some((s) => s.in_stock) ||
              systemData.fans_only.some((c) => c.fans.some((f) => f.in_stock)))
          if (!hasOffers) {
            systemData = null
          }
        }
        if (systemData) {
          upsellShownRef.current = true
          setWizardData(systemData)
          setShowWizard(true)
        } else if (upsellItems.length > 0) {
          upsellShownRef.current = true
          setShowUpsell(true)
        }
      }
    } catch {
      setAddError("Couldn't add to cart — please try again")
    } finally {
      setIsAdding(false)
    }
  }

  return (
    <>
      <div className="flex flex-col gap-y-2" ref={actionsRef}>
        <div>
          {(product.variants?.length ?? 0) > 1 && (
            <div className="flex flex-col gap-y-4">
              {(product.options || []).map((option) => {
                return (
                  <div key={option.id}>
                    <OptionSelect
                      option={option}
                      current={options[option.id]}
                      updateOption={setOptionValue}
                      title={option.title ?? ""}
                      visibleValues={visibleOptionValues[option.id]}
                      data-testid="product-options"
                      disabled={!!disabled || isAdding}
                    />
                  </div>
                )
              })}
              <Divider />
            </div>
          )}
        </div>

        {needsFittings && (
          <RequiredFittings
            fittings={fittingOptions}
            inletId={inletFittingId}
            outletId={outletFittingId}
            onInletChange={setInletFittingId}
            onOutletChange={setOutletFittingId}
            disabled={!!disabled || isAdding}
          />
        )}

        <ProductPrice product={product} variant={selectedVariant} />

        {inletFitting && outletFitting && (
          <Text
            className="text-small-regular text-ui-fg-subtle"
            data-testid="fittings-total"
          >
            +{" "}
            {convertToLocale({
              amount: fittingsTotal,
              currency_code:
                inletFitting.currency_code ??
                outletFitting.currency_code ??
                "usd",
            })}{" "}
            fittings
          </Text>
        )}

        <FinancingMessage amount={financingAmount} />

        <ShipCutoffBanner />

        {isBackordered && selectedVariant && (
          <BackorderPanel variant={selectedVariant} />
        )}

        {hasFreeBilletCap && (
          <div
            className="flex items-center gap-x-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800"
            data-testid="free-billet-cap-badge"
          >
            Includes FREE Billet Radiator Cap
          </div>
        )}

        <Button
          onClick={handleAddToCart}
          disabled={
            !inStock ||
            !selectedVariant ||
            !!disabled ||
            isAdding ||
            !isValidVariant ||
            !fittingsSelected
          }
          variant="primary"
          className="w-full h-10"
          isLoading={isAdding}
          data-testid="add-product-button"
        >
          {!selectedVariant && (product.variants?.length ?? 0) > 1
            ? "Select options"
            : selectedVariant && !inStock
            ? "Out of stock"
            : !fittingsSelected
            ? "Choose your fittings"
            : isBackordered
            ? "Backorder Now"
            : "Add to cart"}
        </Button>
        {addError && (
          <Text
            role="alert"
            className="text-small-regular text-red-600"
            data-testid="add-to-cart-error"
          >
            {addError}
          </Text>
        )}
        <Text
          className="txt-compact-xsmall text-ui-fg-muted"
          data-testid="expedited-shipping-note"
        >
          Expedited shipping available at checkout &mdash; overnight options
          for when the project can&rsquo;t wait.
        </Text>
        <MobileActions
          product={product}
          variant={selectedVariant}
          options={options}
          updateOptions={setOptionValue}
          visibleOptionValues={visibleOptionValues}
          inStock={inStock}
          backordered={isBackordered}
          handleAddToCart={handleAddToCart}
          isAdding={isAdding}
          addError={addError}
          show={!inView}
          optionsDisabled={!!disabled || isAdding}
          fittingsRequired={!fittingsSelected}
        />
      </div>
      {upsellItems.length > 0 && (
        <UpsellModal
          isOpen={showUpsell}
          close={() => setShowUpsell(false)}
          products={upsellItems}
        />
      )}
      {wizardData && (
        <SystemWizard
          isOpen={showWizard}
          close={() => setShowWizard(false)}
          data={wizardData}
        />
      )}
    </>
  )
}
